import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
    ArrowLeft, 
    Plus,
    X,
    Loader2,
    Calendar,
    Link as LinkIcon,
    DollarSign
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const GENRE_OPTIONS = [
    "Pop", "Rock", "Hip-Hop", "R&B", "Electronic", "Jazz", "Classical",
    "Country", "Folk", "Indie", "Alternative", "Soul", "Funk", "Reggae",
    "Blues", "Metal", "Punk", "Ambient", "Cinematic", "World", "Latin"
];

const CATEGORY_OPTIONS = [
    { value: "Film & TV", label: "Film & TV" },
    { value: "Commercial", label: "Commercial / Advertising" },
    { value: "Video Game", label: "Video Game" },
    { value: "Podcast", label: "Podcast / Audio" },
    { value: "Social Media", label: "Social Media Content" },
    { value: "Corporate", label: "Corporate / Brand" },
    { value: "Trailer", label: "Trailer / Promo" },
    { value: "Documentary", label: "Documentary" },
    { value: "Web Series", label: "Web Series" },
    { value: "Other", label: "Other" }
];

const FILE_TYPE_OPTIONS = [
    { value: ".mp3", label: "MP3" },
    { value: ".wav", label: "WAV" },
    { value: ".aiff", label: "AIFF" },
    { value: ".flac", label: "FLAC" }
];

export default function CreateProjectPage() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [submitting, setSubmitting] = useState(false);
    
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        category: '',
        genres: [],
        reference_links: [],
        accepted_file_types: ['.mp3', '.wav'],
        budget: '',
        notes: '',
        deadline: ''
    });

    const [newLink, setNewLink] = useState({ text: '', href: '' });

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const toggleGenre = (genre) => {
        setFormData(prev => ({
            ...prev,
            genres: prev.genres.includes(genre)
                ? prev.genres.filter(g => g !== genre)
                : [...prev.genres, genre]
        }));
    };

    const toggleFileType = (type) => {
        setFormData(prev => ({
            ...prev,
            accepted_file_types: prev.accepted_file_types.includes(type)
                ? prev.accepted_file_types.filter(t => t !== type)
                : [...prev.accepted_file_types, type]
        }));
    };

    const addReferenceLink = () => {
        if (!newLink.href) return;
        
        // Add https if missing
        let href = newLink.href;
        if (!href.startsWith('http://') && !href.startsWith('https://')) {
            href = 'https://' + href;
        }
        
        setFormData(prev => ({
            ...prev,
            reference_links: [...prev.reference_links, { 
                text: newLink.text || href, 
                href 
            }]
        }));
        setNewLink({ text: '', href: '' });
    };

    const removeReferenceLink = (index) => {
        setFormData(prev => ({
            ...prev,
            reference_links: prev.reference_links.filter((_, i) => i !== index)
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.category) {
            toast.error('Please select a category');
            return;
        }
        
        if (formData.genres.length === 0) {
            toast.error('Please select at least one genre');
            return;
        }
        
        if (!formData.deadline) {
            toast.error('Please set a deadline');
            return;
        }
        
        setSubmitting(true);
        
        try {
            const payload = {
                ...formData,
                budget: formData.budget ? parseFloat(formData.budget) : null
            };
            
            const response = await fetch(`${API}/poster/projects`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(payload)
            });
            
            if (response.ok) {
                const project = await response.json();
                toast.success('Project created successfully!');
                navigate(`/poster/projects/${project.id}`);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to create project');
            }
        } catch (error) {
            toast.error('Error creating project');
        } finally {
            setSubmitting(false);
        }
    };

    // Get minimum date (today)
    const today = new Date().toISOString().split('T')[0];

    return (
        <Layout>
            <div className="max-w-3xl mx-auto">
                {/* Back button */}
                <Link 
                    to="/poster/dashboard" 
                    className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-white mb-6 transition-colors"
                >
                    <ArrowLeft size={18} />
                    <span>Back to projects</span>
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-[#1A1A1A] border border-[#333] rounded-xl p-8"
                >
                    <h1 className="text-2xl font-bold text-white mb-2">Create New Project</h1>
                    <p className="text-[#A3A3A3] mb-8">Post a sync opportunity for artists to submit to</p>

                    <form onSubmit={handleSubmit} className="space-y-6">
                        {/* Title */}
                        <div>
                            <Label className="text-white mb-2 block">Project Title *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => handleChange('title', e.target.value)}
                                required
                                className="bg-[#262626] border-[#404040] text-white"
                                placeholder="e.g., Indie Film Score - Emotional Piano Pieces"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <Label className="text-white mb-2 block">Description *</Label>
                            <Textarea
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                required
                                rows={4}
                                className="bg-[#262626] border-[#404040] text-white resize-none"
                                placeholder="Describe what you're looking for, the mood, style, and any specific requirements..."
                            />
                        </div>

                        {/* Category */}
                        <div>
                            <Label className="text-white mb-2 block">Category *</Label>
                            <select
                                value={formData.category}
                                onChange={(e) => handleChange('category', e.target.value)}
                                required
                                className="w-full h-10 px-3 rounded-md bg-[#262626] border border-[#404040] text-white focus:outline-none focus:ring-2 focus:ring-[#F51E1E] focus:border-transparent"
                            >
                                <option value="" disabled>Select a category...</option>
                                {CATEGORY_OPTIONS.map((cat) => (
                                    <option key={cat.value} value={cat.value}>
                                        {cat.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Genres */}
                        <div>
                            <Label className="text-white mb-3 block">Genres *</Label>
                            <div className="flex flex-wrap gap-2">
                                {GENRE_OPTIONS.map((genre) => (
                                    <button
                                        key={genre}
                                        type="button"
                                        onClick={() => toggleGenre(genre)}
                                        className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                                            formData.genres.includes(genre)
                                                ? 'bg-[#F51E1E] text-white'
                                                : 'bg-[#262626] text-[#888] hover:text-white hover:bg-[#333]'
                                        }`}
                                    >
                                        {genre}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Deadline */}
                        <div>
                            <Label className="text-white mb-2 block">Deadline *</Label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    type="date"
                                    value={formData.deadline}
                                    onChange={(e) => handleChange('deadline', e.target.value)}
                                    min={today}
                                    required
                                    className="bg-[#262626] border-[#404040] text-white pl-10"
                                />
                            </div>
                        </div>

                        {/* Accepted File Types */}
                        <div>
                            <Label className="text-white mb-3 block">Accepted File Types</Label>
                            <div className="flex flex-wrap gap-2">
                                {FILE_TYPE_OPTIONS.map((type) => (
                                    <button
                                        key={type.value}
                                        type="button"
                                        onClick={() => toggleFileType(type.value)}
                                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                                            formData.accepted_file_types.includes(type.value)
                                                ? 'bg-[#F51E1E] text-white'
                                                : 'bg-[#262626] text-[#888] hover:text-white hover:bg-[#333]'
                                        }`}
                                    >
                                        {type.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Budget */}
                        <div>
                            <Label className="text-white mb-2 block">Budget (optional)</Label>
                            <div className="relative">
                                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#666]" />
                                <Input
                                    type="number"
                                    value={formData.budget}
                                    onChange={(e) => handleChange('budget', e.target.value)}
                                    min="0"
                                    step="100"
                                    className="bg-[#262626] border-[#404040] text-white pl-10"
                                    placeholder="0"
                                />
                            </div>
                            <p className="text-xs text-[#666] mt-1">Leave empty if budget is negotiable or undisclosed</p>
                        </div>

                        {/* Reference Links */}
                        <div>
                            <Label className="text-white mb-2 block">Reference Links (optional)</Label>
                            <p className="text-xs text-[#666] mb-3">Add Spotify, YouTube, or other links to reference tracks</p>
                            
                            {formData.reference_links.length > 0 && (
                                <div className="space-y-2 mb-3">
                                    {formData.reference_links.map((link, idx) => (
                                        <div key={idx} className="flex items-center gap-2 bg-[#262626] rounded-lg p-2">
                                            <LinkIcon className="w-4 h-4 text-[#666]" />
                                            <span className="text-sm text-white flex-1 truncate">{link.text}</span>
                                            <button
                                                type="button"
                                                onClick={() => removeReferenceLink(idx)}
                                                className="text-[#666] hover:text-red-400"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            
                            <div className="space-y-2">
                                <div className="flex gap-2">
                                    <Input
                                        value={newLink.href}
                                        onChange={(e) => setNewLink(prev => ({ ...prev, href: e.target.value }))}
                                        className="bg-[#262626] border-[#404040] text-white flex-1"
                                        placeholder="Paste URL (YouTube, Spotify, etc.)"
                                    />
                                    <Button
                                        type="button"
                                        onClick={addReferenceLink}
                                        variant="outline"
                                        className="border-[#404040] text-white hover:bg-[#333]"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </Button>
                                </div>
                                <Input
                                    value={newLink.text}
                                    onChange={(e) => setNewLink(prev => ({ ...prev, text: e.target.value }))}
                                    className="bg-[#262626] border-[#404040] text-white"
                                    placeholder="Link name (optional) - e.g., 'Reference - Hans Zimmer'"
                                />
                            </div>
                        </div>

                        {/* Notes */}
                        <div>
                            <Label className="text-white mb-2 block">Additional Notes (optional)</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                rows={3}
                                className="bg-[#262626] border-[#404040] text-white resize-none"
                                placeholder="Any additional information for artists..."
                            />
                        </div>

                        {/* Submit */}
                        <div className="flex gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => navigate('/poster/dashboard')}
                                className="flex-1 border-[#404040] text-white hover:bg-[#333]"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={submitting}
                                className="flex-1 bg-[#F51E1E] hover:bg-[#D41212] text-white"
                            >
                                {submitting ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    'Create Project'
                                )}
                            </Button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </Layout>
    );
}
